/**
 * Custom transform gizmo — a visual + input shell around the command buffer.
 *
 * Renders axis-colored handles for translate, rotate, and scale operations.
 * On drag, calls enterCommand → applyCommandLive → confirmCommand, routing
 * all transform math through the existing command buffer system.
 *
 * Supports transform orientations (Global/Local/Normal/View) by rotating the
 * visual handles and mapping axis grabs to the appropriate constraint vectors.
 *
 * Single global instance rendered in Viewport3D.tsx.
 */

import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useShallow } from "zustand/react/shallow";
import {
  composeSceneObjectWorldMatrix,
  composeSceneObjectWorldQuaternion,
} from "../../app/collectionGraph.ts";
import { useStore } from "../../app/store.ts";
import { visibleHeight } from "../../app/storeCommandOps/transformMath.ts";
import { dlog } from "../../debug/debugLogger.ts";
import { storeKeyToEntityId, useEntityRegistry } from "../../ecs/index.ts";
import { getBone } from "../../mannequin/boneRegistry.ts";
import { getConstraint } from "../../mannequin/constraints.ts";
import { computeSelectionNormal } from "../../modeling/meshOps.ts";
import { usesTopologyPreview } from "../../modeling/operatorParams.ts";
import { computePivot } from "../../modeling/pivot.ts";
import { createRafCoalescer } from "../../rafCoalescer.ts";
import { useUiInteractionStore } from "../../ui/uiInteractionStore.ts";
import { getConstrainedPosition } from "../followConstraintRegistry.ts";
import { useDispose } from "../hooks/useDispose.ts";
import {
  EDGE_PAN_DECAY,
  EDGE_PAN_HOLD_MS,
  EDGE_PAN_MAX_SPEED,
  EDGE_PAN_MIN_ENGAGE_VEL,
  EDGE_PAN_ZONE_PX,
  hasTouchScreen,
  PRECISION_HOLD_MS,
  PRECISION_SCALE,
  PRECISION_STILL_THRESHOLD_SQ,
} from "../interactionConstants.ts";
import { makeScratch } from "../scratch.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import { viewportSupportsSceneInteraction } from "../viewportInteractionPolicy.ts";
import {
  computeOrientationQuat,
  mapHandleToConstraint,
} from "./gizmoOrientation.ts";
import { setGizmoPivotWorld } from "./ProportionalInfluenceRing.tsx";
import { RotationArcOverlay } from "./RotationArcOverlay.tsx";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

// Detected once at module load — coarse-pointer devices get larger grab handles.
const IS_TOUCH = hasTouchScreen();

/** Screen px/sec velocity toward which the edge-pan will lerp, based on cursor proximity. */
function computeEdgePanTarget(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { x: number; y: number } {
  let vx = 0,
    vy = 0;
  const fromRight = rect.right - clientX;
  const fromLeft = clientX - rect.left;
  if (fromRight < EDGE_PAN_ZONE_PX) {
    vx =
      EDGE_PAN_MAX_SPEED *
      Math.max(0, Math.min(1, 1 - fromRight / EDGE_PAN_ZONE_PX));
  } else if (fromLeft < EDGE_PAN_ZONE_PX) {
    vx =
      -EDGE_PAN_MAX_SPEED *
      Math.max(0, Math.min(1, 1 - fromLeft / EDGE_PAN_ZONE_PX));
  }
  const fromBottom = rect.bottom - clientY;
  const fromTop = clientY - rect.top;
  if (fromBottom < EDGE_PAN_ZONE_PX) {
    vy =
      EDGE_PAN_MAX_SPEED *
      Math.max(0, Math.min(1, 1 - fromBottom / EDGE_PAN_ZONE_PX));
  } else if (fromTop < EDGE_PAN_ZONE_PX) {
    vy =
      -EDGE_PAN_MAX_SPEED *
      Math.max(0, Math.min(1, 1 - fromTop / EDGE_PAN_ZONE_PX));
  }
  return { x: vx, y: vy };
}

/**
 * Convert a virtual screen-space cursor delta (px) into a world-space camera
 * pan vector that tracks the object being dragged.
 *
 * Uses the actual gizmo pivot world position so the worldPerPx and axis
 * projection exactly match applyCommandLive's own calculations, preventing
 * the object from drifting on screen as the camera pans.
 *
 * For axis-constrained transforms the camera moves only along the constraint
 * axis; for free transforms it pans in the camera plane.
 */
function computeEdgePanWorldDelta(
  virtualDx: number,
  virtualDy: number,
  camera: THREE.Camera,
  pivotWorld: THREE.Vector3,
  rect: DOMRect,
  axis: string | null | undefined,
  orientationQuat: THREE.Quaternion,
): THREE.Vector3 {
  const dist = camera.position.distanceTo(pivotWorld);
  const vh = visibleHeight(camera, dist);
  const worldPerPx = vh / rect.height;

  const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
  const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);

  if (!axis || axis === "free" || axis === "normal") {
    // Free translation: pan in the camera plane (mirrors applyCommandLive free path).
    return new THREE.Vector3()
      .addScaledVector(right, virtualDx * worldPerPx)
      .addScaledVector(up, -virtualDy * worldPerPx);
  }

  // Axis-constrained: project the virtual screen delta onto the constraint
  // axis screen direction, then return the matching world-space vector.
  const localAxis = new THREE.Vector3(
    axis === "x" ? 1 : 0,
    axis === "y" ? 1 : 0,
    axis === "z" ? 1 : 0,
  );
  const worldAxis = localAxis.applyQuaternion(orientationQuat).normalize();

  // Project axis to NDC from the actual pivot — same origin as applyCommandLive.
  const p0 = pivotWorld.clone().project(camera as THREE.PerspectiveCamera);
  const p1 = pivotWorld
    .clone()
    .add(worldAxis)
    .project(camera as THREE.PerspectiveCamera);
  const axisDirNDC = new THREE.Vector2(p1.x - p0.x, p1.y - p0.y);
  if (axisDirNDC.length() < 1e-6) return new THREE.Vector3(); // axis into/out of camera

  axisDirNDC.normalize();

  // Project the virtual cursor delta (converted to NDC) onto the axis direction.
  const ndcDx = (virtualDx / rect.width) * 2;
  const ndcDy = -(virtualDy / rect.height) * 2; // screen Y is inverted in NDC
  const proj = ndcDx * axisDirNDC.x + ndcDy * axisDirNDC.y;

  return worldAxis.multiplyScalar(proj * vh);
}

import {
  AXIS_COLORS,
  AXIS_COLORS_HOVER,
  DEFAULT_OPACITY,
  DIM_OPACITY,
  GIZMO_SCREEN_PX,
  PLANE_QUAD_OFFSET,
} from "./constants.ts";
import {
  ARROW_HEAD,
  ARROW_SHAFT,
  CENTER_SPHERE,
  HIT_ARROW,
  HIT_CENTER,
  HIT_PLANE,
  HIT_RING,
  HIT_SCALE,
  HIT_SCALE_TOUCH,
  HIT_TOUCH_HANDLE,
  PLANE_QUAD,
  POSE_CENTER_SPHERE,
  POSE_HIT_CENTER,
  ROTATE_RING,
  SCALE_BOX,
  SCALE_BOX_TOUCH,
  SCREEN_RING,
  TOUCH_HANDLE_SPHERE,
} from "./gizmoGeometry.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GizmoAxis = "x" | "y" | "z" | "xy" | "xz" | "yz" | "free";

// Euler rotations to orient +Y-aligned geometry along each axis
const AXIS_ROTATIONS: Record<string, THREE.Euler> = {
  x: new THREE.Euler(0, 0, -Math.PI / 2),
  y: new THREE.Euler(0, 0, 0),
  z: new THREE.Euler(Math.PI / 2, 0, 0),
};

// Rotation for ring geometry (torus lies in XY plane by default)
// To rotate around X, ring lies in YZ → rotate 90° around Y
// To rotate around Y, ring lies in XZ → rotate 90° around X
// To rotate around Z, ring lies in XY → identity
const RING_ROTATIONS: Record<string, THREE.Euler> = {
  x: new THREE.Euler(0, Math.PI / 2, 0),
  y: new THREE.Euler(Math.PI / 2, 0, 0),
  z: new THREE.Euler(0, 0, 0),
};

// Plane quad positions and rotations for two-axis constraints
const PLANE_CONFIGS: Record<
  string,
  { position: THREE.Vector3; rotation: THREE.Euler }
> = {
  xy: {
    position: new THREE.Vector3(PLANE_QUAD_OFFSET, PLANE_QUAD_OFFSET, 0),
    rotation: new THREE.Euler(0, 0, 0),
  },
  xz: {
    position: new THREE.Vector3(PLANE_QUAD_OFFSET, 0, PLANE_QUAD_OFFSET),
    rotation: new THREE.Euler(Math.PI / 2, 0, 0),
  },
  yz: {
    position: new THREE.Vector3(0, PLANE_QUAD_OFFSET, PLANE_QUAD_OFFSET),
    rotation: new THREE.Euler(0, Math.PI / 2, 0),
  },
};

// ---------------------------------------------------------------------------
// Materials cache — bounded singleton.
// ---------------------------------------------------------------------------
//
// The key space is {x, y, z, free} × hovered∈{t,f} × dimmed∈{t,f} = 16 entries.
// Materials live for the module's lifetime by design, matching the explicit
// PRECISION_LANE_MATERIALS pool below; nothing else owns them, so there is no
// dispose path. If a new axis key is ever added it must remain in this fixed
// set — otherwise the cache grows unboundedly.

const materialCache = new Map<string, THREE.MeshBasicMaterial>();

// ---------------------------------------------------------------------------
// Per-frame scratch objects — allocated once, reused every useFrame tick.
// Avoids the continuous GC churn of allocating short-lived Vector3/Quaternion
// objects at 60 fps inside useFrame callbacks and event handlers.
// ---------------------------------------------------------------------------

const tg = makeScratch({
  gizmoWorldPos: "vec3", // bone world position (pose mode)
  gizmoPivotWorld: "vec3", // gizmo pivot world position (every frame)
  zeroPivotVec: "vec3", // constant (0,0,0) for hidden-gizmo early return
  edgePanPivot: "vec3", // scratch for edge-pan pivot (rAF loop)
  objectMatrix: "mat4", // object-local → world transform (edit mode)
  editPivotWorld: "vec3", // edit mode pivot transformed to world space
});

function getAxisMaterial(
  axis: string,
  hovered: boolean,
  dimmed: boolean,
): THREE.MeshBasicMaterial {
  const key = `${axis}-${hovered}-${dimmed}`;
  let mat = materialCache.get(key);
  if (!mat) {
    const color = hovered
      ? (AXIS_COLORS_HOVER[axis] ?? 0xffffff)
      : (AXIS_COLORS[axis] ?? 0xaaaaaa);
    mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: dimmed ? DIM_OPACITY : DEFAULT_OPACITY,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    materialCache.set(key, mat);
  }
  return mat;
}

const invisibleMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
});

// Dragging mutates the command buffer every pointer move, which re-renders this
// component at interaction rate. Keep these resources module-scoped so the
// precision-lane overlay does not allocate fresh GPU geometry every drag frame.
const PRECISION_LANE_GEO = new THREE.CylinderGeometry(0.055, 0.055, 1000, 8);
const PRECISION_LANE_MATERIALS: Record<
  "x" | "y" | "z",
  THREE.MeshBasicMaterial
> = {
  x: new THREE.MeshBasicMaterial({
    color: AXIS_COLORS.x,
    transparent: true,
    opacity: 0.2,
    depthTest: false,
    depthWrite: false,
  }),
  y: new THREE.MeshBasicMaterial({
    color: AXIS_COLORS.y,
    transparent: true,
    opacity: 0.2,
    depthTest: false,
    depthWrite: false,
  }),
  z: new THREE.MeshBasicMaterial({
    color: AXIS_COLORS.z,
    transparent: true,
    opacity: 0.2,
    depthTest: false,
    depthWrite: false,
  }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TransformGizmo() {
  const mode = useStore((s) => s.mode);
  const transformMode = useStore((s) => s.transformMode);
  const selectedObjectTargets = useStore((s) => s.selectedObjectTargets);
  const editMode = useStore((s) => s.editMode);
  const poseMode = useStore((s) => s.poseMode);
  const pivotReference = useStore((s) => s.pivotReference);
  const registry = useEntityRegistry();
  const blendFiles = useStore((s) => s.blendFiles);
  const collections = useStore((s) => s.collections);
  const transformOrientation = useStore((s) => s.transformOrientation);
  const {
    commandType,
    commandAxis,
    commandNormalX,
    commandNormalY,
    commandNormalZ,
  } = useStore(
    useShallow((s) => ({
      commandType: s.commandBuffer?.type ?? null,
      commandAxis: s.commandBuffer?.axis ?? null,
      commandNormalX: s.commandBuffer?.normalVector?.[0] ?? null,
      commandNormalY: s.commandBuffer?.normalVector?.[1] ?? null,
      commandNormalZ: s.commandBuffer?.normalVector?.[2] ?? null,
    })),
  );
  const showSceneInteraction = useStore((s) =>
    viewportSupportsSceneInteraction(s.workspaceMode),
  );
  const isSculptMode = useStore((s) => s.sculptMode != null);
  const isSketchMode = useStore((s) => s.sketchMode != null);
  const pendingAxis = useStore((s) => s.pendingAxis);
  // 3D cursor — when selected, the gizmo targets it directly (translation only).
  const cursor3DSelected = useStore((s) => s.cursor3D.selected);
  const cursor3DPosition = useStore((s) => s.cursor3D.position);

  const { camera, size: viewportSize } = useThree();

  const groupRef = useRef<THREE.Group>(null);
  const orientedGroupRef = useRef<THREE.Group>(null);
  const hoveredRef = useRef<GizmoAxis | null>(null);
  const draggingRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const activeHandleAxisRef = useRef<"x" | "y" | "z" | null>(null);

  const commandNormalVector = useMemo<[number, number, number] | null>(() => {
    if (
      commandNormalX == null ||
      commandNormalY == null ||
      commandNormalZ == null
    )
      return null;
    return [commandNormalX, commandNormalY, commandNormalZ];
  }, [commandNormalX, commandNormalY, commandNormalZ]);

  // Precision mode: activated after holding still on a handle for PRECISION_HOLD_MS,
  // or immediately when Shift is held during a drag.
  const precisionModeRef = useRef(false);
  const precisionAnchorRef = useRef({ x: 0, y: 0 });
  const currentPointerRef = useRef({ x: 0, y: 0 });
  const precisionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the two independent sources that can activate precision mode so that
  // releasing Shift only exits precision if the stillness timer hasn't also fired.
  const precisionShiftHeldRef = useRef(false);
  const precisionTimerFiredRef = useRef(false);
  // Lane meshes keyed by axis — toggled visible by useFrame when precision is active.
  const precisionLaneMeshRefs = useRef<
    Partial<Record<"x" | "y" | "z", THREE.Mesh>>
  >({});

  // Edge-pan state: smoothly translate the camera when the pointer is near the
  // viewport boundary during a gizmo drag. Requires a continuous dwell of
  // EDGE_PAN_HOLD_MS inside the zone before engaging — prevents incidental
  // edge brushes from triggering an unwanted camera pan.
  const edgePanVelRef = useRef({ x: 0, y: 0 }); // target velocity (screen px/sec)
  const edgePanCurVelRef = useRef({ x: 0, y: 0 }); // smoothed current velocity
  const edgePanVirtualOffsetRef = useRef({ x: 0, y: 0 }); // cumulative virtual cursor shift
  const edgePanRafRef = useRef<number | null>(null);
  const edgePanHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const edgePanEngagedRef = useRef(false);

  // -----------------------------------------------------------------------
  // Visibility
  // -----------------------------------------------------------------------

  const inEditMode = mode === "edit" && editMode != null;
  const inPoseMode = poseMode != null;
  const hasBoneSelected =
    inPoseMode && (poseMode!.selectedBones?.size ?? 0) > 0;
  const hasEditSelection = inEditMode && editMode!.selectedVertices.size > 0;
  const hasObjectSelection = selectedObjectTargets.length > 0;
  const isTransformOrEdit = mode === "transform" || mode === "edit";

  const activeOperator = useStore((s) => s.activeOperator);

  // Hide during modal edit commands that own the interaction.
  const isSpecialCommand =
    commandType === "edgeSlide" ||
    (commandType ? usesTopologyPreview(commandType) : false);

  // Hide when a viewport-driven edit tool is active from the tool palette.
  const isOperatorGizmoActive =
    activeOperator === "extrude" ||
    activeOperator === "extrude_along_normals" ||
    activeOperator === "extrude_manifold" ||
    activeOperator === "extrude_individual" ||
    activeOperator === "inset_faces" ||
    activeOperator === "bevel" ||
    activeOperator === "shrink_fatten" ||
    activeOperator === "skin_radius" ||
    activeOperator === "surface_box" ||
    activeOperator === "surface_cylinder" ||
    activeOperator === "surface_poly";

  const visible =
    (isTransformOrEdit || hasBoneSelected || cursor3DSelected) &&
    transformMode !== "refImage" &&
    (cursor3DSelected ||
      hasEditSelection ||
      hasBoneSelected ||
      (!inEditMode && !inPoseMode && hasObjectSelection)) &&
    !isSpecialCommand &&
    !isOperatorGizmoActive &&
    showSceneInteraction &&
    !isSculptMode &&
    !isSketchMode;

  // -----------------------------------------------------------------------
  // Pivot computation
  // -----------------------------------------------------------------------

  const editObjMap = useStore((s) => s.sceneObjects);
  const editObj = useMemo(() => {
    if (!inEditMode) return null;
    return editObjMap.get(editMode!.objectId) ?? null;
  }, [inEditMode, editMode, editObjMap]);

  const pivot = useMemo((): [number, number, number] => {
    // Cursor is the highest-priority gizmo target — its position IS the pivot,
    // bypassing edit/pose/object selection resolution entirely.
    if (cursor3DSelected) {
      return [cursor3DPosition[0], cursor3DPosition[1], cursor3DPosition[2]];
    }

    if (inEditMode && editObj && editMode) {
      const positions: [number, number, number][] = [];
      if (editMode.isCurve && editMode.curveEditVertices) {
        // Curve edit: read from curveEditVertices with curve index layout
        const ev = editMode.curveEditVertices;
        for (const vi of editMode.selectedVertices) {
          const pi = Math.floor(vi / 3);
          const comp = vi % 3;
          const base = pi * 9 + comp * 3;
          positions.push([ev[base], ev[base + 1], ev[base + 2]]);
        }
      } else {
        const verts = editObj.vertices;
        for (const vi of editMode.selectedVertices) {
          positions.push([verts[vi * 3], verts[vi * 3 + 1], verts[vi * 3 + 2]]);
        }
      }
      // Cursor pivot in edit mode needs the world-space cursor converted to
      // the object-local vertex frame. The inverse world matrix
      // (inverse of `collection.transform ∘ obj local`) backs out both the
      // parent collection's transform and the object's local transform in one
      // step — crucial when the edit target is a collection member, where
      // `editObj.rotation/scale` alone is the *local* frame only.
      let cursorLocal: [number, number, number] | undefined;
      if (pivotReference === "cursor") {
        const worldMat = composeSceneObjectWorldMatrix(
          editObjMap,
          collections,
          editMode.objectId,
          new THREE.Matrix4(),
        );
        if (worldMat) {
          const v = new THREE.Vector3(
            cursor3DPosition[0],
            cursor3DPosition[1],
            cursor3DPosition[2],
          ).applyMatrix4(worldMat.invert());
          cursorLocal = [v.x, v.y, v.z];
        }
      }
      return computePivot(positions, pivotReference, cursorLocal);
    }

    // Object mode: centroid of selected objects via ECS.
    // For follow-constrained objects the stored position is the offset from the
    // target, not the visual world position — use the registry value (written by
    // PrimitiveRenderer each frame) when available so the pivot starts at the
    // right place. This is always one frame behind for the initial render, which
    // is corrected immediately by the per-frame useFrame override below.
    const positions: [number, number, number][] = [];
    for (const target of selectedObjectTargets) {
      if (target.kind === "sceneObject") {
        const cp = getConstrainedPosition(target.id);
        if (cp) {
          positions.push(cp);
        } else {
          const entity = registry.get(storeKeyToEntityId(target.id));
          const t = entity?.components.transform;
          if (t) positions.push(t.position as [number, number, number]);
        }
      } else if (target.kind === "blendEntry") {
        const gt = blendFiles.get(target.id)?.groupTransform;
        if (gt) positions.push(gt.position);
      } else if (target.kind === "collection") {
        const gt = collections.get(target.id)?.transform;
        if (gt) positions.push(gt.position);
      }
    }
    return computePivot(positions, pivotReference, cursor3DPosition);
  }, [
    inEditMode,
    editMode,
    editObj,
    editObjMap,
    registry,
    selectedObjectTargets,
    pivotReference,
    cursor3DSelected,
    cursor3DPosition,
    blendFiles,
    collections,
  ]);

  const objectQuat = useMemo(() => {
    if (inEditMode && editObj && editMode) {
      // World rotation (collection.rotation ∘ child.rotation) — consumed by
      // `computeOrientationQuat` for Local/Normal orientation and by
      // `mapHandleToConstraint` to invert the handle axis back into the
      // object-local frame the command buffer writes vertex deltas in.
      // `editObj.rotation` alone would be the child-local rotation, wrong
      // for collection members.
      const q = composeSceneObjectWorldQuaternion(
        editObjMap,
        collections,
        editMode.objectId,
        new THREE.Quaternion(),
      );
      if (q) return q;
      return new THREE.Quaternion().setFromEuler(
        new THREE.Euler(...editObj.rotation),
      );
    }
    // Object mode: use the first selected object's rotation via ECS
    for (const target of selectedObjectTargets) {
      if (target.kind === "sceneObject") {
        const entity = registry.get(storeKeyToEntityId(target.id));
        const t = entity?.components.transform;
        if (t) {
          return new THREE.Quaternion().setFromEuler(
            new THREE.Euler(...(t.rotation as [number, number, number])),
          );
        }
      } else if (target.kind === "blendEntry") {
        const gt = blendFiles.get(target.id)?.groupTransform;
        if (gt) {
          return new THREE.Quaternion(
            gt.rotation[0],
            gt.rotation[1],
            gt.rotation[2],
            gt.rotation[3],
          );
        }
      } else if (target.kind === "collection") {
        const gt = collections.get(target.id)?.transform;
        if (gt) {
          return new THREE.Quaternion(
            gt.rotation[0],
            gt.rotation[1],
            gt.rotation[2],
            gt.rotation[3],
          );
        }
      }
    }
    return new THREE.Quaternion();
  }, [
    inEditMode,
    editObj,
    editMode,
    editObjMap,
    registry,
    selectedObjectTargets,
    blendFiles,
    collections,
  ]);

  // In pose mode, override objectQuat with the bone's world quaternion per-frame
  // so the gizmo axes align with the bone's local coordinate system
  const boneQuatRef = useRef(new THREE.Quaternion());

  // Normal direction for Normal orientation
  const normalDir = useMemo(() => {
    if (!inEditMode || !editObj || !editMode) return null;
    if (editMode.isCurve) return null; // curves don't have mesh normals for orientation
    const n = computeSelectionNormal(
      editObj.vertices,
      editObj.loopNormals,
      editObj.topology,
      editMode.selectedVertices,
    );
    const len = Math.sqrt(n[0] * n[0] + n[1] * n[1] + n[2] * n[2]);
    if (len < 1e-10) return null;
    return new THREE.Vector3(n[0], n[1], n[2]);
  }, [inEditMode, editObj, editMode]);

  // -----------------------------------------------------------------------
  // Per-frame: position, scale, orientation
  // -----------------------------------------------------------------------

  const orientationQuatRef = useRef(new THREE.Quaternion());

  useFrame(() => {
    const group = groupRef.current;
    const oriented = orientedGroupRef.current;
    if (!group || !oriented || !visible) {
      setGizmoPivotWorld(tg.zeroPivotVec, false);
      return;
    }

    // Position at pivot (or bone world position in pose mode, or cursor in cursor mode)
    if (cursor3DSelected) {
      group.position.set(
        cursor3DPosition[0],
        cursor3DPosition[1],
        cursor3DPosition[2],
      );
    } else if (inPoseMode && poseMode?.objectId && poseMode?.activeBone) {
      const bone = getBone(poseMode.objectId, poseMode.activeBone!);
      if (bone) {
        bone.getWorldPosition(tg.gizmoWorldPos);
        group.position.copy(tg.gizmoWorldPos);
        bone.getWorldQuaternion(boneQuatRef.current);
      }
    } else if (inEditMode && editMode) {
      // Edit mode: pivot is in the object-local vertex frame. Compose the
      // full world matrix (collection.transform ∘ obj local) via
      // `composeSceneObjectWorldMatrix` so the gizmo tracks the rendered
      // mesh when the edit target is a collection member. Follow-constrained
      // objects override the translation component to the live constrained
      // position (written by PrimitiveRenderer's own useFrame), since
      // Follow Object isn't valid inside a Collection — composition with the
      // collection transform is a no-op for follow targets.
      const st = useStore.getState();
      const worldMat = composeSceneObjectWorldMatrix(
        st.sceneObjects,
        st.collections,
        editMode.objectId,
        tg.objectMatrix,
      );
      if (worldMat) {
        const editConstrainedPos = getConstrainedPosition(editMode.objectId);
        if (editConstrainedPos) {
          // Overwrite the translation column with the constrained position.
          const e = tg.objectMatrix.elements;
          e[12] = editConstrainedPos[0];
          e[13] = editConstrainedPos[1];
          e[14] = editConstrainedPos[2];
        }
        tg.editPivotWorld
          .set(pivot[0], pivot[1], pivot[2])
          .applyMatrix4(tg.objectMatrix);
        group.position.copy(tg.editPivotWorld);
      }
    } else {
      // Object mode: recompute pivot per-frame using constrained positions when
      // any selected object has active follow constraints. The useMemo pivot is
      // used as a fallback (first frame before the registry is populated).
      let anyConstrained = false;
      for (const target of selectedObjectTargets) {
        if (
          target.kind === "sceneObject" &&
          getConstrainedPosition(target.id)
        ) {
          anyConstrained = true;
          break;
        }
      }
      if (anyConstrained) {
        const livePositions: [number, number, number][] = [];
        for (const target of selectedObjectTargets) {
          if (target.kind === "sceneObject") {
            const cp = getConstrainedPosition(target.id);
            if (cp) {
              livePositions.push(cp);
            } else {
              const entity = registry.get(storeKeyToEntityId(target.id));
              const t = entity?.components.transform;
              if (t) livePositions.push(t.position as [number, number, number]);
            }
          } else if (target.kind === "blendEntry") {
            const gt = blendFiles.get(target.id)?.groupTransform;
            if (gt) livePositions.push(gt.position);
          } else if (target.kind === "collection") {
            const gt = collections.get(target.id)?.transform;
            if (gt) livePositions.push(gt.position);
          }
        }
        const liveP = computePivot(
          livePositions,
          pivotReference,
          cursor3DPosition,
        );
        group.position.set(liveP[0], liveP[1], liveP[2]);
      } else {
        // No follow constraints — use the pre-computed pivot (fast path).
        group.position.set(pivot[0], pivot[1], pivot[2]);
      }
    }

    // Screen-space constant size
    group.getWorldPosition(tg.gizmoPivotWorld);

    // Publish pivot world position for ProportionalInfluenceRing
    setGizmoPivotWorld(tg.gizmoPivotWorld, true);
    group.scale.setScalar(
      screenToWorldRadius(
        GIZMO_SCREEN_PX,
        tg.gizmoPivotWorld,
        camera,
        viewportSize.height,
      ),
    );

    // Orientation: pose mode always uses bone-local axes for rotation gizmo
    const effectiveQuat = hasBoneSelected ? boneQuatRef.current : objectQuat;
    if (cursor3DSelected) {
      // Cursor is a world-space point with no rotation — always global axes.
      orientationQuatRef.current.identity();
    } else if (hasBoneSelected) {
      // Bone-local for pose rotate — copy directly, no clone needed
      orientationQuatRef.current.copy(boneQuatRef.current);
    } else {
      orientationQuatRef.current.copy(
        computeOrientationQuat(
          transformOrientation,
          effectiveQuat,
          normalDir,
          camera,
        ),
      );
    }

    // Orientation is always world-space — no outer transform to compensate for.
    oriented.quaternion.copy(orientationQuatRef.current);
  });

  // -----------------------------------------------------------------------
  // Interaction: drag lifecycle
  // -----------------------------------------------------------------------

  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      // Don't start a new drag if a command is already active
      if (useStore.getState().commandBuffer) return;
      if (draggingRef.current) return;

      const axis = e.object.userData.gizmoAxis as GizmoAxis | undefined;
      if (!axis) return;

      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();

      const state = useStore.getState();

      // Map handle axis to constraint via orientation
      // For two-axis plane constraints (xy, xz, yz), use free for now
      // (the command buffer doesn't have native two-axis support)
      let constraintAxis: string;
      if (axis === "xy" || axis === "xz" || axis === "yz") {
        constraintAxis = "free";
      } else {
        constraintAxis = axis;
      }

      // Pose mode always uses bone-local orientation for rotation
      const effectiveOrientation = state.poseMode?.activeBone
        ? ("local" as const)
        : state.transformOrientation;
      const { axis: cmdAxis, normalVector } = mapHandleToConstraint(
        constraintAxis,
        effectiveOrientation,
        orientationQuatRef.current,
        objectQuat,
        inEditMode,
      );

      // Enter command (gizmo is hidden for refImage, so this is always a valid CommandType)
      if (transformMode === "refImage") return;

      // Route to bone commands when a bone is selected in pose mode
      // Pose mode gizmo always uses boneRotate — translation is via direct joint drag
      let cmdType: import("../../app/storeTypes.ts").CommandType;
      if (state.cursor3D.selected) {
        // Cursor is translation-only regardless of the active transform mode.
        cmdType = "cursorTranslate";
      } else if (state.poseMode?.activeBone) {
        cmdType = "boneRotate";
      } else {
        cmdType =
          transformMode as import("../../app/storeTypes.ts").CommandType;
      }

      // In pose mode with a specific axis ring, pass the bone-local axis
      // so rotation is applied via post-multiply (preserves euler independence).
      const boneLocalAxis =
        state.poseMode?.activeBone &&
        (constraintAxis === "x" ||
          constraintAxis === "y" ||
          constraintAxis === "z")
          ? constraintAxis
          : undefined;

      state.enterCommand(cmdType, {
        axis: cmdAxis,
        normalVector,
        boneLocalAxis,
        gizmoInitiated: true,
      });

      draggingRef.current = true;
      activeHandleAxisRef.current =
        constraintAxis === "x" ||
        constraintAxis === "y" ||
        constraintAxis === "z"
          ? constraintAxis
          : null;

      // Initialise precision-mode state for this drag session.
      precisionModeRef.current = false;
      precisionShiftHeldRef.current = false;
      precisionTimerFiredRef.current = false;
      currentPointerRef.current = {
        x: e.nativeEvent.clientX,
        y: e.nativeEvent.clientY,
      };

      // Reset edge-pan state for this drag session.
      edgePanVelRef.current = { x: 0, y: 0 };
      edgePanCurVelRef.current = { x: 0, y: 0 };
      edgePanVirtualOffsetRef.current = { x: 0, y: 0 };

      // (Re)starts the stillness timer. Called at drag start and whenever
      // the pointer moves fast enough to reset it, allowing precision mode
      // to be entered at any point during a drag — not only at drag start.
      const startPrecisionTimer = () => {
        if (precisionTimerRef.current) clearTimeout(precisionTimerRef.current);
        precisionTimerRef.current = setTimeout(() => {
          precisionTimerRef.current = null;
          precisionTimerFiredRef.current = true;
          // If Shift already activated precision, don't reset the anchor — it
          // was set at the moment Shift was pressed, which is the right origin.
          if (!precisionModeRef.current) {
            // Include virtual offset in anchor so precision scaling is relative
            // to the effective cursor position (not just the real pointer position).
            const vo = edgePanVirtualOffsetRef.current;
            precisionAnchorRef.current = {
              x: currentPointerRef.current.x + vo.x,
              y: currentPointerRef.current.y + vo.y,
            };
            precisionModeRef.current = true;
          }
        }, PRECISION_HOLD_MS);
      };
      startPrecisionTimer();
      const livePreview = createRafCoalescer<{
        screenX: number;
        screenY: number;
        snapGridLevelOffset: number;
      }>((pending) => {
        const opts =
          pending.snapGridLevelOffset === 0
            ? undefined
            : { snapGridLevelOffset: pending.snapGridLevelOffset };
        useStore
          .getState()
          .applyCommandLive(pending.screenX, pending.screenY, opts);
      });

      const scheduleDragPreview = (
        effectiveX: number,
        effectiveY: number,
        snapGridLevelOffset = 0,
      ) => {
        if (precisionModeRef.current) {
          const { x: ax, y: ay } = precisionAnchorRef.current;
          livePreview.schedule({
            screenX: ax + (effectiveX - ax) * PRECISION_SCALE,
            screenY: ay + (effectiveY - ay) * PRECISION_SCALE,
            snapGridLevelOffset,
          });
        } else {
          livePreview.schedule({
            screenX: effectiveX,
            screenY: effectiveY,
            snapGridLevelOffset,
          });
        }
      };

      const flushDragPreview = () => {
        livePreview.flush();
      };

      const currentShiftSnapGridLevelOffset = () =>
        precisionShiftHeldRef.current ? -1 : 0;

      // rAF loop: smoothly pan the camera when the pointer is near a viewport
      // edge, accumulating a virtual cursor offset so the transform continues.
      // The camera tracks the object exactly — same axis, same world distance.
      // Rotation transforms are excluded (rotation doesn't benefit from panning).
      let edgePanLastTime = performance.now();
      const runEdgePan = (timestamp: number) => {
        if (!draggingRef.current) return;

        const dt = Math.min((timestamp - edgePanLastTime) / 1000, 0.05);
        edgePanLastTime = timestamp;

        // Exponential decay lerp toward target velocity
        const alpha = 1 - Math.exp(-EDGE_PAN_DECAY * dt);
        const cur = edgePanCurVelRef.current;
        const tgt = edgePanVelRef.current;
        cur.x += (tgt.x - cur.x) * alpha;
        cur.y += (tgt.y - cur.y) * alpha;

        const cb = useStore.getState().commandBuffer;

        if (
          (Math.abs(cur.x) > EDGE_PAN_MIN_ENGAGE_VEL ||
            Math.abs(cur.y) > EDGE_PAN_MIN_ENGAGE_VEL) &&
          cb &&
          (cb.type === "translate" || cb.type === "boneTranslate")
        ) {
          // A stationary cursor near the viewport edge is an intentional pan hold,
          // not a "hold still" signal — suppress the precision-mode timer.
          if (precisionTimerRef.current) {
            clearTimeout(precisionTimerRef.current);
            precisionTimerRef.current = null;
          }

          const camera = window.__poseref_camera;
          const ctrlsRef = window.__poseref_orbitControls;
          const getRect = window.__poseref_canvasRect;
          const controls = ctrlsRef?.current;

          if (camera && controls && getRect) {
            const rect = getRect();
            // Screen-pixel delta this frame
            const panX = cur.x * dt;
            const panY = cur.y * dt;

            // Use the gizmo's actual pivot world position so worldPerPx and axis
            // projection exactly match applyCommandLive, preventing screen drift.
            const pivotWorld = groupRef.current
              ? groupRef.current.getWorldPosition(tg.edgePanPivot)
              : tg.edgePanPivot.copy(controls.target);

            // World-space camera pan that tracks the object along its constraint axis.
            const worldPan = computeEdgePanWorldDelta(
              panX,
              panY,
              camera,
              pivotWorld,
              rect,
              cb.axis as string | undefined,
              orientationQuatRef.current,
            );
            camera.position.add(worldPan);
            controls.target.add(worldPan);
            controls.update();

            // Accumulate virtual cursor offset so applyCommandLive sees an
            // ever-increasing drag distance, matching the world translation above.
            const vo = edgePanVirtualOffsetRef.current;
            vo.x += panX;
            vo.y += panY;

            // Re-apply the transform with the updated effective cursor position.
            const cp = currentPointerRef.current;
            const efX = cp.x + vo.x;
            const efY = cp.y + vo.y;
            scheduleDragPreview(efX, efY, currentShiftSnapGridLevelOffset());
          }
        }

        edgePanRafRef.current = requestAnimationFrame(runEdgePan);
      };
      edgePanRafRef.current = requestAnimationFrame(runEdgePan);

      // Clears the hold timer and, if edge-pan engagement had already been
      // announced to the chrome-dim store, announces disengagement. Called
      // from every drag-termination path (onUp, cleanupRef, stopEdgePan, and
      // inline when the pointer leaves the zone mid-drag) so the store flag
      // can never survive past the gesture that set it.
      const disengageEdgePanState = () => {
        if (edgePanHoldTimerRef.current) {
          clearTimeout(edgePanHoldTimerRef.current);
          edgePanHoldTimerRef.current = null;
        }
        if (edgePanEngagedRef.current) {
          edgePanEngagedRef.current = false;
          useUiInteractionStore.getState().endEdgePan();
        }
      };

      const stopEdgePan = () => {
        if (edgePanRafRef.current !== null) {
          cancelAnimationFrame(edgePanRafRef.current);
          edgePanRafRef.current = null;
        }
        edgePanVelRef.current = { x: 0, y: 0 };
        edgePanCurVelRef.current = { x: 0, y: 0 };
        edgePanVirtualOffsetRef.current = { x: 0, y: 0 };
        disengageEdgePanState();
      };

      // Shift key handlers — active only for the lifetime of this drag session.
      // Pressing Shift immediately enters precision mode (same 0.25× scaling as
      // the stillness timer, anchored at the cursor position when Shift is pressed).
      // While Shift is held, grid snapping also steps down one zoom level finer.
      // Releasing Shift exits precision only if the stillness timer hasn't also fired.
      const onShiftDown = (ev: KeyboardEvent) => {
        if (ev.key !== "Shift" || precisionShiftHeldRef.current) return;
        precisionShiftHeldRef.current = true;
        if (!precisionModeRef.current) {
          // Cancel the pending stillness timer — Shift takes over immediately.
          if (precisionTimerRef.current) {
            clearTimeout(precisionTimerRef.current);
            precisionTimerRef.current = null;
          }
          const vo = edgePanVirtualOffsetRef.current;
          precisionAnchorRef.current = {
            x: currentPointerRef.current.x + vo.x,
            y: currentPointerRef.current.y + vo.y,
          };
          precisionModeRef.current = true;
        }
        const cb = useStore.getState().commandBuffer;
        if (cb?.mouseOrigin) {
          const vo = edgePanVirtualOffsetRef.current;
          scheduleDragPreview(
            currentPointerRef.current.x + vo.x,
            currentPointerRef.current.y + vo.y,
            -1,
          );
          flushDragPreview();
        }
      };
      const onShiftUp = (ev: KeyboardEvent) => {
        if (ev.key !== "Shift") return;
        precisionShiftHeldRef.current = false;
        // Only exit precision if the stillness timer hasn't independently fired.
        // If it has, precision stays active for the remainder of the drag.
        if (!precisionTimerFiredRef.current) {
          precisionModeRef.current = false;
          // Snap transform back to the real (coarse) pointer position.
          const vo = edgePanVirtualOffsetRef.current;
          scheduleDragPreview(
            currentPointerRef.current.x + vo.x,
            currentPointerRef.current.y + vo.y,
          );
          flushDragPreview();
        }
      };

      // Attach window-level listeners for this drag session
      const onMove = (ev: PointerEvent) => {
        const prevX = currentPointerRef.current.x;
        const prevY = currentPointerRef.current.y;
        currentPointerRef.current = { x: ev.clientX, y: ev.clientY };

        // Edge-pan target velocity — disabled for rotation transforms, and
        // gated by a hold-in-zone timer so incidental edge brushes don't
        // trigger camera follow. Once the pointer leaves the zone, engagement
        // is dropped; re-entering re-arms the timer from zero.
        const getRect = window.__poseref_canvasRect;
        const cbType = useStore.getState().commandBuffer?.type;
        const isTranslate =
          cbType === "translate" || cbType === "boneTranslate";
        if (getRect && isTranslate) {
          const tgt = computeEdgePanTarget(ev.clientX, ev.clientY, getRect());
          const inZone = tgt.x !== 0 || tgt.y !== 0;
          if (inZone) {
            if (
              !edgePanEngagedRef.current &&
              edgePanHoldTimerRef.current === null
            ) {
              edgePanHoldTimerRef.current = setTimeout(() => {
                edgePanHoldTimerRef.current = null;
                edgePanEngagedRef.current = true;
                useUiInteractionStore.getState().beginEdgePan();
              }, EDGE_PAN_HOLD_MS);
            }
            edgePanVelRef.current = edgePanEngagedRef.current
              ? tgt
              : { x: 0, y: 0 };
          } else {
            disengageEdgePanState();
            edgePanVelRef.current = { x: 0, y: 0 };
          }
        } else {
          disengageEdgePanState();
          edgePanVelRef.current = { x: 0, y: 0 };
        }

        const vo = edgePanVirtualOffsetRef.current;
        const efX = ev.clientX + vo.x;
        const efY = ev.clientY + vo.y;

        if (!precisionModeRef.current) {
          // Reset stillness timer on significant inter-event movement so the
          // user can move fast, pause, and enter precision mid-drag.
          const dx = ev.clientX - prevX;
          const dy = ev.clientY - prevY;
          if (dx * dx + dy * dy > PRECISION_STILL_THRESHOLD_SQ) {
            startPrecisionTimer();
          }
          scheduleDragPreview(efX, efY, currentShiftSnapGridLevelOffset());
        } else {
          scheduleDragPreview(efX, efY, currentShiftSnapGridLevelOffset());
        }
      };
      const onUp = () => {
        if (precisionTimerRef.current) {
          clearTimeout(precisionTimerRef.current);
          precisionTimerRef.current = null;
        }
        precisionModeRef.current = false;
        precisionShiftHeldRef.current = false;
        precisionTimerFiredRef.current = false;
        stopEdgePan();
        flushDragPreview();
        useStore.getState().confirmCommand();
        draggingRef.current = false;
        activeHandleAxisRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        window.removeEventListener("keydown", onShiftDown);
        window.removeEventListener("keyup", onShiftUp);
        cleanupRef.current = null;
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      window.addEventListener("keydown", onShiftDown);
      window.addEventListener("keyup", onShiftUp);
      cleanupRef.current = () => {
        if (precisionTimerRef.current) {
          clearTimeout(precisionTimerRef.current);
          precisionTimerRef.current = null;
        }
        precisionModeRef.current = false;
        precisionShiftHeldRef.current = false;
        precisionTimerFiredRef.current = false;
        stopEdgePan();
        livePreview.cancel();
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        window.removeEventListener("keydown", onShiftDown);
        window.removeEventListener("keyup", onShiftUp);
      };
    },
    [transformMode, objectQuat, inEditMode],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  // Also clean up if command is cancelled externally (e.g. Escape)
  useEffect(() => {
    if (!commandType && draggingRef.current) {
      draggingRef.current = false;
      activeHandleAxisRef.current = null;
      // cleanupRef already cancels the precision timer and removes listeners.
      cleanupRef.current?.();
      cleanupRef.current = null;
    }
  }, [commandType]);

  useEffect(() => {
    dlog.trace("js", "gizmo.transform.state", "Transform gizmo command state", {
      commandType: commandType ?? "idle",
      axis: commandAxis ?? "none",
      normal: commandNormalVector,
    });
  }, [commandType, commandAxis, commandNormalVector]);

  // -----------------------------------------------------------------------
  // Hover tracking
  // -----------------------------------------------------------------------

  const handlePointerOver = useCallback((e: ThreeEvent<PointerEvent>) => {
    const axis = e.object.userData.gizmoAxis as GizmoAxis | undefined;
    if (axis && !draggingRef.current) {
      hoveredRef.current = axis;
      // Force re-render for hover feedback would be expensive.
      // Instead we rely on useFrame to update materials.
    }
    e.stopPropagation();
  }, []);

  const handlePointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!draggingRef.current) {
      hoveredRef.current = null;
    }
    e.stopPropagation();
  }, []);

  // -----------------------------------------------------------------------
  // Handle material updates via useFrame (avoids React re-renders for hover)
  // -----------------------------------------------------------------------

  const handleRefs = useRef<Map<string, THREE.Mesh[]>>(new Map());
  const ringGroupRefs = useRef<Map<string, THREE.Group>>(new Map());

  const pruneHandleRefs = useCallback((axis?: string) => {
    const pruneAxis = (key: string) => {
      const list = handleRefs.current.get(key);
      if (!list) return;
      let write = 0;
      for (let i = 0; i < list.length; i++) {
        const mesh = list[i]!;
        if (mesh.parent === null) continue;
        list[write++] = mesh;
      }
      list.length = write;
      if (write === 0) handleRefs.current.delete(key);
    };

    if (axis) {
      pruneAxis(axis);
      return;
    }
    for (const key of handleRefs.current.keys()) pruneAxis(key);
  }, []);

  const registerHandle = useCallback(
    (axis: string) => (mesh: THREE.Mesh | null) => {
      if (!mesh) {
        queueMicrotask(() => pruneHandleRefs(axis));
        return;
      }
      let list = handleRefs.current.get(axis);
      if (!list) {
        list = [];
        handleRefs.current.set(axis, list);
      }
      if (!list.includes(mesh)) list.push(mesh);
    },
    [pruneHandleRefs],
  );

  useFrame(() => {
    pruneHandleRefs();
    if (!visible) return;
    const hovered = hoveredRef.current;
    const activeAxis = activeHandleAxisRef.current;
    const emphasis = activeAxis ?? hovered;
    const anyEmphasis = emphasis != null;

    // Compute valid rotation axes from the active bone's constraint (in the
    // animation loop to avoid React re-renders that trigger shadow rebuilds).
    const st = useStore.getState();
    const activeBoneName = st.poseMode?.activeBone;
    const boneConstraint = activeBoneName
      ? getConstraint(activeBoneName)
      : null;
    const isPoseRotate = st.poseMode != null && st.transformMode === "rotate";

    // Hide ring groups for invalid axes (e.g. hinge joints show only one ring)
    for (const [axis, group] of ringGroupRefs.current) {
      const axisInvalid =
        isPoseRotate &&
        boneConstraint?.type === "hinge" &&
        axis !== boneConstraint.axis;
      group.visible = !axisInvalid;
    }

    for (const [axis, meshes] of handleRefs.current) {
      // Determine the base axis color key (xy → use "free", etc.)
      const colorKey =
        axis === "xy" || axis === "xz" || axis === "yz" ? "free" : axis;
      const isThisEmphasized = emphasis === axis;
      const dimmed = anyEmphasis && !isThisEmphasized;
      // Hide inactive axes entirely while actively dragging
      const hidden = activeAxis != null && !isThisEmphasized;
      const mat = getAxisMaterial(colorKey, isThisEmphasized, dimmed);
      for (const mesh of meshes) {
        if (mesh.material !== invisibleMaterial) {
          mesh.material = mat;
          mesh.visible = !hidden;
        }
      }
    }

    // Show precision lane only on the active constraint axis while in precision mode.
    const laneAxis = precisionModeRef.current
      ? activeHandleAxisRef.current
      : null;
    for (const ax of ["x", "y", "z"] as const) {
      const lane = precisionLaneMeshRefs.current[ax];
      if (lane) lane.visible = ax === laneAxis;
    }
  });

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    applyGizmoRenderOrder(group, 999);
  });

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!visible) return null;

  // In pose mode, transformMode drives which handles to show:
  // "translate" = IK move handles, "rotate" = FK rotation handles
  // Cursor is translation-only — force-suppress rotate/scale handles when selected.
  const isTranslate = cursor3DSelected || transformMode === "translate";
  const isRotate = !cursor3DSelected && transformMode === "rotate";
  const isScale =
    !cursor3DSelected && !hasBoneSelected && transformMode === "scale";

  // When an axis constraint is pending (set via tool palette), filter
  // handles to only show the constrained axis.
  const constrainedAxis =
    pendingAxis === "x" || pendingAxis === "y" || pendingAxis === "z"
      ? pendingAxis
      : null;
  const visibleAxes: readonly ("x" | "y" | "z")[] = constrainedAxis
    ? [constrainedAxis]
    : ["x", "y", "z"];
  const showMultiAxis = constrainedAxis == null;

  // Shared event props for hit meshes
  const hitEvents = {
    onPointerDown: handlePointerDown,
    onPointerOver: handlePointerOver,
    onPointerOut: handlePointerOut,
  };

  // Gizmo lives entirely in world space — the pivot is transformed to world
  // space in useFrame for all modes. The outer group is a neutral JSX container.
  return (
    <group userData={{ excludeFromExport: true }}>
      <group ref={groupRef} renderOrder={999}>
        <group ref={orientedGroupRef}>
          {/* ---- Translate handles ---- */}
          {isTranslate && (
            <>
              {visibleAxes.map((axis) => (
                <group key={axis} rotation={AXIS_ROTATIONS[axis]}>
                  {/* Visual */}
                  <mesh ref={registerHandle(axis)} geometry={ARROW_SHAFT}>
                    <meshBasicMaterial
                      color={AXIS_COLORS[axis]}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  <mesh
                    ref={registerHandle(axis)}
                    geometry={IS_TOUCH ? TOUCH_HANDLE_SPHERE : ARROW_HEAD}
                  >
                    <meshBasicMaterial
                      color={AXIS_COLORS[axis]}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  {/* Hit volume — large sphere at tip on touch for an easy grab target */}
                  <mesh
                    geometry={IS_TOUCH ? HIT_TOUCH_HANDLE : HIT_ARROW}
                    material={invisibleMaterial}
                    userData={{ gizmoAxis: axis, isGizmoHandle: true }}
                    {...hitEvents}
                  />
                </group>
              ))}

              {/* Plane quads for two-axis constraints (not shown in pose mode or when axis-constrained) */}
              {showMultiAxis &&
                !inPoseMode &&
                (["xy", "xz", "yz"] as const).map((plane) => {
                  const cfg = PLANE_CONFIGS[plane];
                  return (
                    <group
                      key={plane}
                      position={cfg.position}
                      rotation={cfg.rotation}
                    >
                      <mesh ref={registerHandle(plane)} geometry={PLANE_QUAD}>
                        <meshBasicMaterial
                          color={0xaaaaaa}
                          transparent
                          opacity={0.25}
                          side={THREE.DoubleSide}
                          depthTest={false}
                          depthWrite={false}
                        />
                      </mesh>
                      <mesh
                        geometry={HIT_PLANE}
                        material={invisibleMaterial}
                        userData={{ gizmoAxis: plane, isGizmoHandle: true }}
                        {...hitEvents}
                      />
                    </group>
                  );
                })}

              {/* Center sphere — enlarged in pose mode for easy free-axis grab (hidden when axis-constrained) */}
              {showMultiAxis && (
                <>
                  <mesh
                    ref={registerHandle("free")}
                    geometry={inPoseMode ? POSE_CENTER_SPHERE : CENTER_SPHERE}
                  >
                    <meshBasicMaterial
                      color={0xaaaaaa}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  <mesh
                    geometry={inPoseMode ? POSE_HIT_CENTER : HIT_CENTER}
                    material={invisibleMaterial}
                    userData={{ gizmoAxis: "free", isGizmoHandle: true }}
                    {...hitEvents}
                  />
                </>
              )}
            </>
          )}

          {/* ---- Rotate handles ---- */}
          {isRotate && (
            <>
              {visibleAxes.map((axis) => (
                <group
                  key={axis}
                  rotation={RING_ROTATIONS[axis]}
                  ref={(g) => {
                    if (g) ringGroupRefs.current.set(axis, g);
                    else ringGroupRefs.current.delete(axis);
                  }}
                >
                  <mesh ref={registerHandle(axis)} geometry={ROTATE_RING}>
                    <meshBasicMaterial
                      color={AXIS_COLORS[axis]}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  <mesh
                    geometry={HIT_RING}
                    material={invisibleMaterial}
                    userData={{ gizmoAxis: axis, isGizmoHandle: true }}
                    {...hitEvents}
                  />
                </group>
              ))}

              {/* Pose mode: enlarged center sphere (matches translate gizmo, double-tap target) */}
              {/* Non-pose: screen-aligned ring for free rotation */}
              {/* Hidden when axis-constrained */}
              {showMultiAxis &&
                (inPoseMode ? (
                  <>
                    <mesh
                      ref={registerHandle("free")}
                      geometry={POSE_CENTER_SPHERE}
                    >
                      <meshBasicMaterial
                        color={0xaaaaaa}
                        transparent
                        opacity={DEFAULT_OPACITY}
                        depthTest={false}
                        depthWrite={false}
                      />
                    </mesh>
                    <mesh
                      geometry={POSE_HIT_CENTER}
                      material={invisibleMaterial}
                      userData={{ gizmoAxis: "free", isGizmoHandle: true }}
                      {...hitEvents}
                    />
                  </>
                ) : (
                  <Billboard>
                    <mesh ref={registerHandle("free")} geometry={SCREEN_RING}>
                      <meshBasicMaterial
                        color={0xaaaaaa}
                        transparent
                        opacity={0.3}
                        depthTest={false}
                        depthWrite={false}
                      />
                    </mesh>
                    <mesh
                      geometry={SCREEN_RING}
                      material={invisibleMaterial}
                      userData={{ gizmoAxis: "free", isGizmoHandle: true }}
                      {...hitEvents}
                    />
                  </Billboard>
                ))}
            </>
          )}

          {/* ---- Rotation constraint arc overlay (pose mode only) ---- */}
          {isRotate && inPoseMode && (
            <RotationArcOverlay activeHandleAxisRef={activeHandleAxisRef} />
          )}

          {/* ---- Scale handles ---- */}
          {isScale && (
            <>
              {visibleAxes.map((axis) => (
                <group key={axis} rotation={AXIS_ROTATIONS[axis]}>
                  <mesh ref={registerHandle(axis)} geometry={ARROW_SHAFT}>
                    <meshBasicMaterial
                      color={AXIS_COLORS[axis]}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  <mesh
                    ref={registerHandle(axis)}
                    geometry={IS_TOUCH ? SCALE_BOX_TOUCH : SCALE_BOX}
                  >
                    <meshBasicMaterial
                      color={AXIS_COLORS[axis]}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  {/* Hit volume — wider cylinder on touch for easier grab */}
                  <mesh
                    geometry={IS_TOUCH ? HIT_SCALE_TOUCH : HIT_SCALE}
                    material={invisibleMaterial}
                    userData={{ gizmoAxis: axis, isGizmoHandle: true }}
                    {...hitEvents}
                  />
                </group>
              ))}

              {/* Center cube for uniform scale (hidden when axis-constrained) */}
              {showMultiAxis && (
                <>
                  <mesh ref={registerHandle("free")} geometry={CENTER_SPHERE}>
                    <meshBasicMaterial
                      color={0xaaaaaa}
                      transparent
                      opacity={DEFAULT_OPACITY}
                      depthTest={false}
                      depthWrite={false}
                    />
                  </mesh>
                  <mesh
                    geometry={HIT_CENTER}
                    material={invisibleMaterial}
                    userData={{ gizmoAxis: "free", isGizmoHandle: true }}
                    {...hitEvents}
                  />
                </>
              )}
            </>
          )}

          {/* ---- Precision mode lane — thin cylinder along the active constraint axis ---- */}
          {(["x", "y", "z"] as const).map((axis) => (
            <mesh
              key={`precision-lane-${axis}`}
              ref={(m) => {
                if (m) precisionLaneMeshRefs.current[axis] = m;
                else delete precisionLaneMeshRefs.current[axis];
              }}
              rotation={AXIS_ROTATIONS[axis]}
              geometry={PRECISION_LANE_GEO}
              material={PRECISION_LANE_MATERIALS[axis]}
              visible={false}
              renderOrder={998}
            />
          ))}
        </group>

        {/* ---- Axis constraint line (outside oriented group to avoid double-rotation) ---- */}
        <AxisConstraintLine
          axis={commandAxis}
          normalVector={commandNormalVector}
          orientationQuat={orientationQuatRef}
          objectQuat={objectQuat}
          inEditMode={inEditMode}
        />
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Axis constraint line — drawn through the pivot when an axis is active
// ---------------------------------------------------------------------------

const CONSTRAINT_LINE_HALF = 500; // in gizmo-local units (screen-space scaled)

const constraintLineGeo = new THREE.BufferGeometry();
constraintLineGeo.setAttribute(
  "position",
  new THREE.BufferAttribute(
    new Float32Array([0, -CONSTRAINT_LINE_HALF, 0, 0, CONSTRAINT_LINE_HALF, 0]),
    3,
  ),
);

function AxisConstraintLine({
  axis,
  normalVector,
  orientationQuat,
  objectQuat,
  inEditMode,
}: {
  axis: string | null;
  normalVector: [number, number, number] | null;
  orientationQuat: React.RefObject<THREE.Quaternion>;
  objectQuat: THREE.Quaternion;
  inEditMode: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null);

  // Per-frame: apply the correct rotation for the constraint line.
  // This component is OUTSIDE the oriented group, so we must rotate manually.
  // The gizmo is world-space, so cardinal orientations are applied directly.
  // Normal-vector geometry is stored in object-local space for edit mode and
  // world space for object mode; apply objectQuat in edit mode to convert.
  useFrame(() => {
    if (!groupRef.current || !axis) return;

    if (axis === "normal") {
      // Normal geometry vertices are in object-local space (edit) or world space
      // (object mode). Rotate by objectQuat in edit mode to reach world space.
      if (inEditMode) {
        groupRef.current.quaternion.copy(objectQuat);
      } else {
        groupRef.current.quaternion.identity();
      }
    } else {
      // Cardinal axes: orientation quaternion is world-space; apply directly.
      groupRef.current.quaternion.copy(orientationQuat.current);
    }
  });

  // For normal axis, build geometry along the normal vector direction
  const normalGeo = useMemo(() => {
    if (axis !== "normal" || !normalVector) return null;
    const [nx, ny, nz] = normalVector;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(
        new Float32Array([
          -nx * CONSTRAINT_LINE_HALF,
          -ny * CONSTRAINT_LINE_HALF,
          -nz * CONSTRAINT_LINE_HALF,
          nx * CONSTRAINT_LINE_HALF,
          ny * CONSTRAINT_LINE_HALF,
          nz * CONSTRAINT_LINE_HALF,
        ]),
        3,
      ),
    );
    return geo;
  }, [axis, normalVector]);

  // Dispose normal geometry when it changes (new constraint axis normal) or on unmount.
  useDispose(normalGeo);

  if (!axis) return null;

  if (axis === "normal") {
    if (!normalGeo) return null;
    return (
      <group ref={groupRef}>
        <lineSegments geometry={normalGeo} renderOrder={998}>
          <lineBasicMaterial
            color={0xcc66ff}
            transparent
            opacity={0.7}
            depthTest={false}
            depthWrite={false}
          />
        </lineSegments>
      </group>
    );
  }

  const color = AXIS_COLORS[axis] ?? 0xaaaaaa;
  const rotation = AXIS_ROTATIONS[axis];
  if (!rotation) return null;

  return (
    <group ref={groupRef}>
      <lineSegments
        geometry={constraintLineGeo}
        rotation={rotation}
        renderOrder={998}
      >
        <lineBasicMaterial
          color={color}
          transparent
          opacity={0.7}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Billboard helper — always faces the camera
// ---------------------------------------------------------------------------

function Billboard({ children }: { children: React.ReactNode }) {
  const ref = useRef<THREE.Group>(null);
  const { camera } = useThree();

  useFrame(() => {
    if (ref.current) {
      ref.current.quaternion.copy(camera.quaternion);
    }
  });

  return <group ref={ref}>{children}</group>;
}
